import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence, useMotionValue, useTransform, PanInfo, animate } from "framer-motion";
import {
  Check,
  X,
  Volume2,
  VolumeX,
  Flag,
  BookOpen,
  PartyPopper,
  ChevronRight,
  RotateCcw,
  Megaphone,
  ArrowLeft,
  ArrowDownLeft,
} from "lucide-react";
import { Card } from "@/types/linguadeck";
import { useGamification } from "@/contexts/GamificationContext";
import { useSoundStore } from "@/stores/useSoundStore";
import { getCardSkinStyles } from "@/lib/cardSkins";
import { playAudio } from "@/lib/audioUtils";

function shuffleArray<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function generateOptions(currentCard: Card, allCards: Card[]): Card[] {
  const others = allCards.filter((c) => c !== currentCard && (c.front || "").trim());
  const count = Math.min(4, 1 + others.length);
  const distractors = shuffleArray(others).slice(0, count - 1);
  return shuffleArray([currentCard, ...distractors]);
}

interface LearnModeProps {
  cards: Card[];
  onBack?: () => void;
  onSessionEnd?: (progressPct: number) => void;
  /** Deck lang for TTS (optional) */
  deckLang?: string;
  /** Deck ID for adaptive progress (required if onCardResult provided) */
  deckId?: string;
  /** Per-card result callback for adaptive learning */
  onCardResult?: (cardId: string, correct: boolean, skipped: boolean) => void;
}

export default function LearnMode({ cards, onBack, onSessionEnd, deckLang = "en", deckId, onCardResult }: LearnModeProps) {
  const { t } = useTranslation();
  const { equippedCardSkin } = useGamification();
  const { isVoiceEnabled, voiceVolume, toggleVoice } = useSoundStore();
  const cardSkinStyles = getCardSkinStyles(equippedCardSkin);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<Card | null>(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [isSkipped, setIsSkipped] = useState(false);
  const [answerHistory, setAnswerHistory] = useState<{ index: number; skipped: boolean }[]>([]);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const speakingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const filteredCards = useMemo(
    () => cards.filter((c) => (c.front || "").trim() && (c.back || "").trim()),
    [cards]
  );

  const currentCard = filteredCards[currentIndex];
  const options = useMemo(
    () => (currentCard ? generateOptions(currentCard, filteredCards) : []),
    [currentCard, currentIndex, filteredCards]
  );

  const total = filteredCards.length;
  const filledCount = currentIndex + (isAnswered ? 1 : 0);
  const pct = total > 0 ? Math.round((filledCount / total) * 100) : 0;

  const advanceToNext = useCallback(() => {
    setAnswerHistory((prev) => [...prev, { index: currentIndex, skipped: isSkipped }]);
    setCurrentIndex((i) => i + 1);
    setSelectedAnswer(null);
    setIsAnswered(false);
    setIsSkipped(false);
  }, [currentIndex, isSkipped]);

  const nextCard = useCallback(() => {
    advanceToNext();
  }, [advanceToNext]);

  const prevCard = useCallback(() => {
    if (answerHistory.length === 0) return;
    const last = answerHistory[answerHistory.length - 1];
    setAnswerHistory((prev) => prev.slice(0, -1));
    setCurrentIndex(last.index);
    setSelectedAnswer(null);
    setIsAnswered(false);
    setIsSkipped(false);
  }, [answerHistory]);

  const restart = useCallback(() => {
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setIsAnswered(false);
    setIsSkipped(false);
    setAnswerHistory([]);
  }, []);

  const handleNotSure = useCallback(() => {
    if (isAnswered) return;
    if (currentCard?.id && deckId && onCardResult) {
      onCardResult(currentCard.id, false, true);
    }
    setIsSkipped(true);
    setIsAnswered(true);
    setSelectedAnswer(null);
  }, [isAnswered, currentCard?.id, deckId, onCardResult]);

  const handleSelect = useCallback(
    (option: Card) => {
      if (isAnswered) return;
      const correct = option === currentCard;
      if (currentCard?.id && deckId && onCardResult) {
        onCardResult(currentCard.id, correct, false);
      }
      setSelectedAnswer(option);
      setIsAnswered(true);
    },
    [isAnswered, currentCard, deckId, onCardResult]
  );

  // Keyboard: any key after answer to continue; arrows for prev
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isAnswered) {
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          prevCard();
        }
        return;
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        prevCard();
        return;
      }
      if (e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        nextCard();
      } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        nextCard();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isAnswered, nextCard, prevCard]);

  const progressRef = useRef({ currentIndex, isAnswered, total, onSessionEnd });
  progressRef.current = { currentIndex, isAnswered, total, onSessionEnd };
  useEffect(
    () => () => {
      const { currentIndex: i, isAnswered: a, total: tot, onSessionEnd: cb } = progressRef.current;
      if (tot > 0 && cb) cb(Math.min(100, Math.round(((i + (a ? 1 : 0)) / tot) * 100)));
    },
    []
  );

  // Swipe gestures — 1:1 copy from TestStudyView (working Tests mode)
  const SWIPE_OFFSET_THRESHOLD = 80;
  const SWIPE_VELOCITY_THRESHOLD = 400;
  const EXIT_OFFSET = 450;
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-250, 0, 250], [-12, 0, 12]);
  const opacityLeft = useTransform(x, [-250, -SWIPE_OFFSET_THRESHOLD, 0], [0.95, 0.5, 0]);
  const opacityRight = useTransform(x, [0, SWIPE_OFFSET_THRESHOLD, 250], [0, 0.5, 0.95]);
  const scaleLeft = useTransform(x, [-200, -60, 0], [1.15, 0.85, 0.7]);
  const scaleRight = useTransform(x, [0, 60, 200], [0.7, 0.85, 1.15]);
  // Dynamic background tint: green on swipe right, orange on swipe left (max opacity ~0.25)
  const opacityGreen = useTransform(x, [0, 80, 200], [0, 0.5, 1]);
  const opacityOrange = useTransform(x, [-200, -80, 0], [1, 0.5, 0]);
  const isExitingRef = useRef(false);

  const resetCardPosition = () => x.set(0);

  const handleDragEnd = async (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (isExitingRef.current) return;
    const offset = info.offset.x;
    const velocity = info.velocity.x;
    const isRightSwipe = offset > SWIPE_OFFSET_THRESHOLD || velocity > SWIPE_VELOCITY_THRESHOLD;
    const isLeftSwipe = offset < -SWIPE_OFFSET_THRESHOLD || velocity < -SWIPE_VELOCITY_THRESHOLD;
    const spring = { type: "spring" as const, stiffness: 300, damping: 25 };

    if (isRightSwipe) {
      if (isAnswered) {
        isExitingRef.current = true;
        await animate(x, EXIT_OFFSET, spring);
        nextCard();
        isExitingRef.current = false;
        resetCardPosition();
      } else {
        animate(x, 0, spring);
      }
    } else if (isLeftSwipe) {
      if (!isAnswered) {
        handleNotSure();
        animate(x, 0, spring);
      } else {
        isExitingRef.current = true;
        await animate(x, -EXIT_OFFSET, spring);
        prevCard();
        isExitingRef.current = false;
        resetCardPosition();
      }
    } else {
      animate(x, 0, spring);
    }
  };

  useEffect(() => resetCardPosition(), [currentIndex]);

  // Clear speaking timeout on unmount
  useEffect(() => () => {
    if (speakingTimeoutRef.current) clearTimeout(speakingTimeoutRef.current);
  }, []);

  /** Infer source/target lang from deck when cards lack frontLang/backLang */
  const inferredSourceLang = useMemo(
    () => filteredCards.find((c) => (c.backLang ?? "").trim())?.backLang ?? deckLang,
    [filteredCards, deckLang]
  );
  const inferredTargetLang = useMemo(
    () => filteredCards.find((c) => (c.frontLang ?? "").trim())?.frontLang ?? deckLang,
    [filteredCards, deckLang]
  );

  /** Dynamic TTS: question (back) in source lang when no answer; ALWAYS correct answer (front) in target lang when answered. */
  const handleSpeak = useCallback(() => {
    if (!isVoiceEnabled || !currentCard) return;
    const clearSpeaking = () => {
      if (speakingTimeoutRef.current) {
        clearTimeout(speakingTimeoutRef.current);
        speakingTimeoutRef.current = null;
      }
      setIsSpeaking(false);
    };
    const deckLangForTts = inferredTargetLang || deckLang;
    if (isAnswered) {
      const text = (currentCard.front || "").trim();
      if (text) {
        setIsSpeaking(true);
        void playAudio({
          card: currentCard,
          side: "front",
          deckLang: deckLangForTts,
          volume: voiceVolume ?? 1,
          onEnd: clearSpeaking,
          onError: clearSpeaking,
        });
        speakingTimeoutRef.current = setTimeout(clearSpeaking, 5000);
      }
    } else {
      const text = (currentCard.back || "").trim();
      if (text) {
        setIsSpeaking(true);
        void playAudio({
          card: currentCard,
          side: "back",
          deckLang: inferredSourceLang || deckLang,
          volume: voiceVolume ?? 1,
          onEnd: clearSpeaking,
          onError: clearSpeaking,
        });
        speakingTimeoutRef.current = setTimeout(clearSpeaking, 5000);
      }
    }
  }, [currentCard, isAnswered, inferredSourceLang, inferredTargetLang, deckLang, isVoiceEnabled, voiceVolume]);

  // Empty state
  if (filteredCards.length === 0) {
    return (
      <div className="flex flex-col h-[100dvh] w-full bg-background pt-[env(safe-area-inset-top)]">
        <div className="flex-1 flex flex-col items-center justify-center px-4">
          <BookOpen className="w-14 h-14 mx-auto mb-4 text-slate-300 dark:text-slate-500" />
          <p className="text-lg font-medium text-[hsl(var(--foreground))] mb-2">
            {t("learnMode.noCards")}
          </p>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">
            {t("learnMode.noCardsHint")}
          </p>
          {onBack && (
            <button
              type="button"
              onClick={() => { onSessionEnd?.(0); onBack(); }}
              className="px-6 py-2.5 rounded-xl font-semibold bg-[hsl(var(--primary))] text-white hover:opacity-90 transition-opacity"
            >
              {t("shared.previous")}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Finished state
  const isFinished = currentIndex >= total;
  if (isFinished) {
    return (
      <div className="flex flex-col h-[100dvh] w-full bg-background pt-[env(safe-area-inset-top)]">
        <div className="flex-1 flex flex-col items-center justify-center px-4">
          <PartyPopper className="w-16 h-16 mb-4 text-amber-500 dark:text-amber-400" />
          <h2 className="text-2xl font-semibold text-[hsl(var(--foreground))] mb-2">
            {t("learnMode.finished")}
          </h2>
          <p className="text-[hsl(var(--muted-foreground))] mb-8">
            {t("learnMode.finishedSub", { count: total })}
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={restart}
              className="px-6 py-3 rounded-xl font-semibold bg-[hsl(var(--primary))] text-white hover:opacity-90 transition-opacity"
            >
              {t("learnMode.restart")}
            </button>
            {onBack && (
              <button
                type="button"
                onClick={() => { onSessionEnd?.(100); onBack(); }}
                className="px-6 py-3 rounded-xl font-semibold border border-[hsl(var(--border))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--bg3))] transition-colors"
              >
                {t("shared.previous")}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!currentCard || options.length === 0) return null;

  const nextCardData = currentIndex + 1 < total ? filteredCards[currentIndex + 1] : null;
  const questionLen = currentCard.back.length;
  const questionSizeClass = questionLen > 80 ? "text-xl" : questionLen > 40 ? "text-2xl" : "text-3xl";

  return (
    <div className="fixed inset-0 z-50 flex flex-col h-[100dvh] w-full bg-background overflow-hidden overscroll-x-none pt-[env(safe-area-inset-top)]" style={{ overscrollBehaviorX: "none" }}>
      {/* Progress bar */}
      <div className="flex-shrink-0 h-[3px] w-full bg-[hsl(var(--bg3))]">
        <motion.div
          className="h-full rounded-r-full"
          style={{
            width: `${pct}%`,
            background: "linear-gradient(90deg, hsl(var(--primary)), hsl(var(--accent2)))",
          }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* Header: Close, Progress, Voice */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 h-12">
        <div className="w-10 flex justify-start">
          {onBack && (
            <button
              type="button"
              onClick={() => { onSessionEnd?.(pct); onBack(); }}
              className="flex items-center justify-center w-10 h-10 rounded-full text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--bg3))] active:opacity-70 transition-colors"
              aria-label={t("studyMode.close")}
            >
              <X size={24} strokeWidth={2} />
            </button>
          )}
        </div>
        <span className="text-sm font-medium text-[hsl(var(--foreground))] tabular-nums">
          {t("studyMode.progress", { current: currentIndex + 1, total })}
        </span>
        <div className="w-10 flex justify-end">
          <button
            type="button"
            onClick={toggleVoice}
            className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
              isVoiceEnabled
                ? "text-[hsl(var(--primary))] hover:bg-[hsl(var(--bg3))]"
                : "text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--bg3))] opacity-70"
            } active:opacity-70`}
            aria-label={isVoiceEnabled ? t("flashcard.audio_on") : t("flashcard.audio_off")}
          >
            {isVoiceEnabled ? <Volume2 size={22} strokeWidth={2} /> : <VolumeX size={22} strokeWidth={2} />}
          </button>
        </div>
      </div>

      {/* Card area with swipe — overscroll-x-none blocks iOS swipe-back */}
      <div className="flex-1 min-h-0 flex flex-col relative px-4 py-2 overscroll-x-none" style={{ perspective: "1200px", overscrollBehaviorX: "none" }}>
        {/* Swipe labels */}
        <motion.div
          className="absolute left-4 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1 z-[60] pointer-events-none origin-center"
          style={{ opacity: opacityLeft, scale: scaleLeft }}
        >
          <span className="text-xl font-bold text-amber-500 dark:text-amber-400 whitespace-nowrap drop-shadow-sm">
            {isAnswered ? t("learnMode.previous") : t("learnMode.notSure")}
          </span>
        </motion.div>
        <motion.div
          className="absolute right-4 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1 z-[60] pointer-events-none origin-center"
          style={{ opacity: opacityRight, scale: scaleRight }}
        >
          <span className="text-xl font-bold text-emerald-600 dark:text-emerald-400 whitespace-nowrap drop-shadow-sm">
            {isAnswered ? t("learnMode.next") : t("learnMode.pickAnswer")}
          </span>
        </motion.div>

        {/* Next card stack */}
        {nextCardData && (
          <div className="absolute inset-0 flex items-center justify-center px-4 py-2" style={{ zIndex: 0 }}>
            <div
              className="w-[92%] max-w-md h-full rounded-2xl border border-[hsl(var(--border))] overflow-hidden flex flex-col"
              style={{
                ...cardSkinStyles,
                background: "hsl(var(--card-bg))",
                transform: "scale(0.94) translateY(8px)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              }}
            >
              <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
                <p className="text-2xl text-[hsl(var(--muted-foreground))]/50 line-clamp-3">{nextCardData.back}</p>
              </div>
            </div>
          </div>
        )}

        {/* Draggable card — 1:1 copy of TestStudyView: same drag props and touchAction */}
        <motion.div
          className="absolute inset-0 flex flex-col px-4 py-2 cursor-grab active:cursor-grabbing z-10"
          style={{ x, rotate, touchAction: "pan-y" }}
          drag="x"
          dragConstraints={{ left: -500, right: 500 }}
          dragElastic={0.7}
          dragDirectionLock
          onDragEnd={handleDragEnd}
        >
          <div
            className="w-[92%] max-w-md flex-1 min-h-[280px] self-center rounded-2xl border border-[hsl(var(--border))] overflow-hidden flex flex-col relative"
            style={{
              ...cardSkinStyles,
              background: "hsl(var(--card-bg))",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            {/* Dynamic background tint overlay — green (swipe right) / orange (swipe left) */}
            <div className="absolute inset-0 rounded-2xl pointer-events-none overflow-hidden z-[1]">
              <motion.div
                className="absolute inset-0 rounded-2xl"
                style={{ backgroundColor: "rgba(34, 197, 94, 0.25)", opacity: opacityGreen }}
              />
              <motion.div
                className="absolute inset-0 rounded-2xl"
                style={{ backgroundColor: "rgba(249, 115, 22, 0.25)", opacity: opacityOrange }}
              />
            </div>
            {/* Image: flex-1 expands to fill space; w-full object-cover, no letterboxing */}
            {currentCard.imageUrl ? (
              <div className="flex-1 min-h-[200px] w-full overflow-hidden rounded-t-2xl">
                <img
                  src={currentCard.imageUrl}
                  alt=""
                  className="h-full w-full object-cover rounded-t-xl"
                />
              </div>
            ) : (
              <div className="flex-1 min-h-[200px]" aria-hidden />
            )}

            {/* Bottom block: Word + TTS + feedback + MCQ + Not sure?/Undo/Next — same structure as TestStudyView (no pointer-events override) */}
            <div className="flex flex-col justify-end flex-shrink-0 px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <div className="flex items-center justify-center gap-3 w-full mb-2">
                <h2 className={`${questionSizeClass} font-bold text-[hsl(var(--foreground))] text-center leading-tight line-clamp-3 flex-1 min-w-0`}>
                  {currentCard.back}
                </h2>
                {isVoiceEnabled && (
                  <div className="flex-shrink-0 flex items-center gap-1.5">
                    <AnimatePresence mode="wait">
                      {isSpeaking && (
                        <motion.span
                          key="speak-arrow"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          transition={{ duration: 0.2 }}
                          className="text-[hsl(var(--primary))] animate-pulse"
                          aria-hidden
                        >
                          {isAnswered ? (
                            <ArrowDownLeft size={16} strokeWidth={2.5} />
                          ) : (
                            <ArrowLeft size={16} strokeWidth={2.5} />
                          )}
                        </motion.span>
                      )}
                    </AnimatePresence>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleSpeak(); }}
                      className="flex items-center justify-center w-9 h-9 rounded-full bg-[hsl(var(--bg3))]/60 text-[hsl(var(--foreground))] hover:bg-[hsl(var(--bg3))] transition-colors"
                      aria-label={t("flashcard.listen")}
                    >
                      <Megaphone size={18} strokeWidth={2} />
                    </button>
                  </div>
                )}
              </div>

              {isAnswered && (isSkipped || (selectedAnswer && selectedAnswer !== currentCard)) && (
                <p className={`text-sm font-bold mb-2 ${
                  isSkipped ? "text-slate-700 dark:text-[hsl(var(--foreground))]" : "text-orange-500 dark:text-orange-400"
                }`}>
                  {isSkipped ? t("learnMode.returnLater") : t("learnMode.stillLearning")}
                </p>
              )}

              <div className="text-sm font-semibold text-[hsl(var(--muted-foreground))] px-1 mb-2">
                {t("shared.question")}
              </div>

              {/* MCQ buttons */}
              <div className="flex flex-col gap-3 w-full">
                {options.map((option, idx) => {
                  const chosen = selectedAnswer === option;
                  const correct = option === currentCard;
                  const userClickedCorrect = isAnswered && correct && chosen && !isSkipped;
                  const showCorrectDashed = isAnswered && correct && (isSkipped || (selectedAnswer !== null && selectedAnswer !== currentCard));
                  const showWrong = isAnswered && !isSkipped && chosen && !correct;

                  let btnClasses = "min-h-[52px] p-3 flex items-center gap-3 w-full rounded-xl border-2 transition-all duration-200 text-left flex-shrink-0 ";
                  let badgeClasses = "w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-sm font-semibold ";

                  if (userClickedCorrect) {
                    btnClasses += "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40 cursor-default";
                    badgeClasses += "bg-emerald-500 text-white";
                  } else if (showCorrectDashed) {
                    btnClasses += "border-emerald-500 border-dashed bg-white dark:bg-[hsl(var(--card-bg))] text-emerald-700 dark:text-emerald-400 cursor-default";
                    badgeClasses += "bg-white dark:bg-[hsl(var(--card-bg))] border border-emerald-400 text-emerald-600 dark:text-emerald-400";
                  } else if (showWrong) {
                    btnClasses += "border-red-400 bg-red-50 dark:bg-red-950/40 cursor-default";
                    badgeClasses += "bg-red-500 text-white";
                  } else if (!isAnswered) {
                    btnClasses += "border-slate-100 dark:border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card-bg))] hover:border-blue-200 dark:hover:border-blue-500/50 hover:bg-blue-50/50 dark:hover:bg-blue-500/10 cursor-pointer active:scale-[0.99]";
                    badgeClasses += "bg-slate-100 dark:bg-[hsl(var(--border))] text-slate-600 dark:text-[hsl(var(--muted-foreground))]";
                  } else {
                    btnClasses += "border-slate-100 dark:border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card-bg))] cursor-default opacity-70";
                    badgeClasses += "bg-slate-100 dark:bg-[hsl(var(--border))] text-slate-600 dark:text-[hsl(var(--muted-foreground))]";
                  }

                  return (
                    <button
                      key={`${option.id ?? idx}-${option.front}`}
                      type="button"
                      disabled={isAnswered}
                      onClick={() => handleSelect(option)}
                      className={btnClasses}
                    >
                      <span className={badgeClasses}>
                        {(userClickedCorrect || showCorrectDashed) ? (
                          <Check size={18} strokeWidth={2.5} />
                        ) : showWrong ? (
                          <X size={18} strokeWidth={2.5} />
                        ) : (
                          idx + 1
                        )}
                      </span>
                      <span className="text-base font-medium text-slate-700 dark:text-[hsl(var(--foreground))] text-left break-words min-w-0">
                        {option.front}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Not sure? / Undo + Next — inside bottom block */}
              <div className="flex items-center justify-between gap-2 pt-4">
                {!isAnswered ? (
                  <button
                    type="button"
                    onClick={handleNotSure}
                    className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-full text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--bg3))] transition-colors"
                  >
                    <Flag size={18} strokeWidth={2} />
                    <span className="text-sm font-medium">{t("learnMode.notSure")}</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={prevCard}
                    disabled={answerHistory.length === 0}
                    className={`flex items-center justify-center w-11 h-11 rounded-full transition-all ${
                      answerHistory.length > 0
                        ? "text-[hsl(var(--foreground))] hover:bg-[hsl(var(--bg3))] active:scale-95"
                        : "text-[hsl(var(--muted-foreground))]/40 cursor-not-allowed"
                    }`}
                    aria-label={t("learnMode.undo")}
                  >
                    <RotateCcw size={22} strokeWidth={2} />
                  </button>
                )}
                <div className="flex-1" />
                {isAnswered && (
                  <button
                    type="button"
                    onClick={nextCard}
                    className="flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold bg-[hsl(var(--primary))] text-white shadow-lg hover:opacity-90 active:scale-95 transition-all"
                  >
                    {t("learnMode.next")}
                    <ChevronRight size={20} strokeWidth={2} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
